import { useEffect } from 'react';
import type { createConversationNavigation } from '@/lib/desktop-shell/conversation-navigation';
import type { useSidebarModel } from '@/lib/desktop-shell/use-sidebar-model';

type HydrationStatus = 'waiting' | 'loading' | 'ready' | 'error';
type SidebarThread = ReturnType<typeof useSidebarModel>['threads'][number];
type SelectThread = ReturnType<typeof createConversationNavigation>['selectThread'];

interface InitialThreadHydrationOptions {
  preview: boolean;
  restoreInitialThread: boolean;
  chatId: string;
  syncBootstrapStatus: 'loading' | 'ready' | 'error';
  hydrationStatus: HydrationStatus;
  setHydrationStatus: React.Dispatch<React.SetStateAction<HydrationStatus>>;
  /** Every conversation that can be opened: the assistants' too, which the sidebar does not list. */
  /**
   * Every conversation that can be opened: an address may name one the sidebar does not list (an
   * assistant's, a group of personas).
   */
  threads: readonly SidebarThread[];
  syncedThreadIds: ReadonlySet<string>;
  selectThread: SelectThread;
  initialChatIdRef: React.MutableRefObject<string>;
  selectionRequestRef: React.MutableRefObject<number>;
  hydrationRequestRef: React.MutableRefObject<number>;
  hydrationAttemptsRef: React.MutableRefObject<number>;
  hydrationRetryTimerRef: React.MutableRefObject<number | null>;
  setWorkspaceSyncError: (error: string | null) => void;
}

/** Restores the initially addressed task after sync bootstrap, with bounded retries. */
export function useInitialThreadHydration({
  preview,
  restoreInitialThread,
  chatId,
  syncBootstrapStatus,
  hydrationStatus,
  setHydrationStatus,
  threads,
  syncedThreadIds,
  selectThread,
  initialChatIdRef,
  selectionRequestRef,
  hydrationRequestRef,
  hydrationAttemptsRef,
  hydrationRetryTimerRef,
  setWorkspaceSyncError,
}: InitialThreadHydrationOptions): void {
  useEffect(
    () => () => {
      if (hydrationRetryTimerRef.current !== null) {
        window.clearTimeout(hydrationRetryTimerRef.current);
      }
    },
    [hydrationRetryTimerRef],
  );

  useEffect(() => {
    // Navigation callbacks can change on every shell render. Once restored (or
    // skipped), this initial-only effect must not publish another state update.
    if (hydrationStatus === 'ready') return;
    if (preview || !restoreInitialThread) {
      setHydrationStatus('ready');
      return;
    }
    if (chatId !== initialChatIdRef.current) {
      hydrationRequestRef.current += 1;
      setHydrationStatus('ready');
      return;
    }
    if (syncBootstrapStatus === 'error') {
      if (hydrationStatus !== 'error') setHydrationStatus('error');
      return;
    }
    if (syncBootstrapStatus !== 'ready' || hydrationStatus !== 'waiting') return;
    const initialThread = threads.find((thread) => thread.id === chatId);
    if (initialThread === undefined || !syncedThreadIds.has(chatId)) {
      setHydrationStatus('ready');
      return;
    }
    const hydrationRequest = ++hydrationRequestRef.current;
    hydrationAttemptsRef.current += 1;
    setHydrationStatus('loading');
    const restoring = selectThread(initialThread, { record: false, restoreCurrent: true });
    const navigationRequest = selectionRequestRef.current;
    void restoring.then((restored) => {
      if (
        hydrationRequestRef.current !== hydrationRequest ||
        selectionRequestRef.current !== navigationRequest
      ) {
        return;
      }
      if (restored) {
        setHydrationStatus('ready');
        setWorkspaceSyncError(null);
        return;
      }
      if (hydrationAttemptsRef.current < 3) {
        hydrationRetryTimerRef.current = window.setTimeout(
          () => {
            hydrationRetryTimerRef.current = null;
            if (hydrationRequestRef.current === hydrationRequest) {
              setHydrationStatus('waiting');
            }
          },
          300 * 2 ** (hydrationAttemptsRef.current - 1),
        );
        return;
      }
      setHydrationStatus('error');
      setWorkspaceSyncError('Unable to restore this conversation. Reload to try again.');
    });
  }, [
    chatId,
    hydrationAttemptsRef,
    hydrationRequestRef,
    hydrationRetryTimerRef,
    hydrationStatus,
    initialChatIdRef,
    preview,
    restoreInitialThread,
    selectThread,
    selectionRequestRef,
    setHydrationStatus,
    setWorkspaceSyncError,
    threads,
    syncBootstrapStatus,
    syncedThreadIds,
  ]);
}
